import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { serviceProviderApi, avvContractApi, processingApi, organizationApi, openPdfExport } from '../services/api';
import {
  ServiceProvider,
  Organization,
  ServiceProviderRole,
  AvvContract,
  AvvStatus,
  AvvContractCreate,
  ProcessingActivity,
  ProcessingStatus,
  RiskLevel
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  TabContainer,
  Tab,
  MessageBox,
  ObjectStatus,
  Icon,
  BusyIndicator,
  MessageStrip,
  Input,
  TextArea,
  CheckBox,
  Label,
  Table,
  TableColumn,
  TableRow,
  TableCell
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

const roleConfig: Record<ServiceProviderRole, { label: string; state: string }> = {
  [ServiceProviderRole.PROCESSOR]: { label: 'Auftragsverarbeiter', state: 'Information' },
  [ServiceProviderRole.JOINT_CONTROLLER]: { label: 'Gem. Verantwortlicher', state: 'Warning' },
  [ServiceProviderRole.RECIPIENT]: { label: 'Empfänger', state: 'None' }
};

const avvStatusConfig: Record<AvvStatus, { label: string; state: string }> = {
  [AvvStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [AvvStatus.ACTIVE]: { label: 'Aktiv', state: 'Success' },
  [AvvStatus.EXPIRED]: { label: 'Abgelaufen', state: 'Error' },
  [AvvStatus.TERMINATED]: { label: 'Beendet', state: 'None' }
};

const processingStatusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

const riskConfig: Record<RiskLevel, { label: string; state: string }> = {
  [RiskLevel.LOW]: { label: 'Niedrig', state: 'Success' },
  [RiskLevel.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [RiskLevel.HIGH]: { label: 'Hoch', state: 'Error' }
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', padding: '0.5rem 0', borderBottom: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
    <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', width: '180px', flexShrink: 0 }}>{label}</Text>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

interface NewAvvForm {
  version: string;
  start_date: string;
  end_date: string;
  subcontractors: string[];
  tom_assessment: string;
  tom_adequate: boolean;
  document_reference: string;
  local_path: string;
}

const defaultAvvForm: NewAvvForm = {
  version: '',
  start_date: '',
  end_date: '',
  subcontractors: [],
  tom_assessment: '',
  tom_adequate: false,
  document_reference: '',
  local_path: ''
};

export default function ServiceProviderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEdit, canDelete } = useUserRole();

  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [avvContracts, setAvvContracts] = useState<AvvContract[]>([]);
  const [linkedActivities, setLinkedActivities] = useState<ProcessingActivity[]>([]);
  const [allActivities, setAllActivities] = useState<ProcessingActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [avvToDelete, setAvvToDelete] = useState<number | null>(null);
  const [showNewAvvForm, setShowNewAvvForm] = useState(false);
  const [newAvvForm, setNewAvvForm] = useState<NewAvvForm>(defaultAvvForm);
  const [subcontractorInput, setSubcontractorInput] = useState('');
  const [savingAvv, setSavingAvv] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [uploadingAvvId, setUploadingAvvId] = useState<number | null>(null);
  const [editingAvvId, setEditingAvvId] = useState<number | null>(null);
  const [editAvvForm, setEditAvvForm] = useState<NewAvvForm>(defaultAvvForm);
  const [editSubInput, setEditSubInput] = useState('');
  const editSubRef = useRef('');

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const orgsResponse = await organizationApi.getAll();
    if (orgsResponse.success && orgsResponse.data && orgsResponse.data.length > 0) {
      const org = orgsResponse.data[0];
      setCurrentOrg(org);
      if (id) {
        const [providerRes, avvRes, linkedRes, allRes] = await Promise.all([
          serviceProviderApi.getById(parseInt(id), org.id),
          avvContractApi.getByProvider(parseInt(id), org.id),
          serviceProviderApi.getLinkedActivities(parseInt(id), org.id),
          processingApi.getByOrganization(org.id)
        ]);
        if (providerRes.success && providerRes.data) {
          setProvider(providerRes.data);
        } else {
          navigate('/service-providers');
        }
        if (avvRes.success && avvRes.data) setAvvContracts(avvRes.data);
        if (linkedRes.success && linkedRes.data) setLinkedActivities(linkedRes.data);
        if (allRes.success && allRes.data) setAllActivities(allRes.data);
      }
    }
    setLoading(false);
  };

  const handleEdit = () => navigate(`/service-providers/${id}/edit`);

  const handleDelete = async () => {
    if (!provider || !currentOrg) return;
    const response = await serviceProviderApi.delete(provider.id, currentOrg.id);
    if (response.success) navigate('/service-providers');
  };

  const handleAvvStatusChange = async (avvId: number, newStatus: AvvStatus) => {
    if (!provider || !currentOrg) return;
    const response = await avvContractApi.updateStatus(provider.id, avvId, newStatus, currentOrg.id);
    if (response.success && response.data) {
      setAvvContracts(prev => prev.map(a => a.id === avvId ? response.data! : a));
    }
  };

  const handleAvvDelete = async () => {
    if (!provider || !currentOrg || avvToDelete === null) return;
    const response = await avvContractApi.delete(provider.id, avvToDelete, currentOrg.id);
    if (response.success) {
      setAvvContracts(prev => prev.filter(a => a.id !== avvToDelete));
      setAvvToDelete(null);
    }
  };

  const handleCreateAvv = async () => {
    if (!provider || !currentOrg) return;
    if (!newAvvForm.version || !newAvvForm.start_date) return;
    setSavingAvv(true);
    const payload: AvvContractCreate = {
      service_provider_id: provider.id,
      version: newAvvForm.version,
      start_date: newAvvForm.start_date,
      end_date: newAvvForm.end_date || undefined,
      subcontractors: newAvvForm.subcontractors,
      tom_assessment: newAvvForm.tom_assessment || undefined,
      tom_adequate: newAvvForm.tom_adequate,
      document_reference: newAvvForm.document_reference || undefined,
      local_path: newAvvForm.local_path || undefined,
      status: AvvStatus.DRAFT
    };
    const response = await avvContractApi.create(provider.id, payload, currentOrg.id);
    if (response.success && response.data) {
      setAvvContracts(prev => [...prev, response.data!]);
      setShowNewAvvForm(false);
      setNewAvvForm(defaultAvvForm);
      setSubcontractorInput('');
    }
    setSavingAvv(false);
  };

  const subInputRef = useRef('');
  const handleAddSubcontractor = () => {
    const value = (subcontractorInput || subInputRef.current).trim();
    if (value && !newAvvForm.subcontractors.includes(value)) {
      setNewAvvForm(f => ({ ...f, subcontractors: [...f.subcontractors, value] }));
      setSubcontractorInput('');
      subInputRef.current = '';
    }
  };

  const handleRemoveSubcontractor = (sub: string) => {
    setNewAvvForm(f => ({ ...f, subcontractors: f.subcontractors.filter(s => s !== sub) }));
  };

  const startEditAvv = (avv: AvvContract) => {
    setEditingAvvId(avv.id);
    setEditAvvForm({
      version: avv.version,
      start_date: avv.start_date,
      end_date: avv.end_date || '',
      document_reference: avv.document_reference || '',
      local_path: avv.local_path || '',
      subcontractors: avv.subcontractors || [],
      tom_assessment: avv.tom_assessment || '',
      tom_adequate: avv.tom_adequate
    });
    setEditSubInput('');
  };

  const handleAddEditSub = () => {
    const val = (editSubInput || editSubRef.current).trim();
    if (val && !editAvvForm.subcontractors.includes(val)) {
      setEditAvvForm(f => ({ ...f, subcontractors: [...f.subcontractors, val] }));
      setEditSubInput('');
      editSubRef.current = '';
    }
  };

  const handleSaveAvvEdit = async () => {
    if (!provider || !currentOrg || !editingAvvId) return;
    setSavingAvv(true);
    const response = await avvContractApi.update(provider.id, editingAvvId, {
      version: editAvvForm.version,
      start_date: editAvvForm.start_date,
      end_date: editAvvForm.end_date || undefined,
      subcontractors: editAvvForm.subcontractors,
      tom_assessment: editAvvForm.tom_assessment || undefined,
      tom_adequate: editAvvForm.tom_adequate,
      document_reference: editAvvForm.document_reference || undefined,
      local_path: editAvvForm.local_path || undefined
    }, currentOrg.id);
    if (response.success && response.data) {
      setAvvContracts(prev => prev.map(a => a.id === editingAvvId ? response.data! : a));
      setEditingAvvId(null);
    }
    setSavingAvv(false);
  };

  const handleUploadDocuments = async (avvId: number, files: FileList | null) => {
    if (!provider || !currentOrg || !files || files.length === 0) return;
    setUploadingAvvId(avvId);
    const fileArray = Array.from(files);
    const response = await avvContractApi.uploadDocuments(provider.id, avvId, currentOrg.id, fileArray);
    if (response.success && response.data) {
      setAvvContracts(prev => prev.map(a => a.id === avvId ? response.data! : a));
    }
    setUploadingAvvId(null);
  };

  const handleDeleteDocument = async (avvId: number, filename: string) => {
    if (!provider || !currentOrg) return;
    const response = await avvContractApi.deleteDocument(provider.id, avvId, currentOrg.id, filename);
    if (response.success && response.data) {
      setAvvContracts(prev => prev.map(a => a.id === avvId ? response.data! : a));
    }
  };

  const handleLinkActivity = async (activityId: number) => {
    if (!provider || !currentOrg) return;
    const response = await serviceProviderApi.linkActivity(provider.id, activityId, currentOrg.id);
    if (response.success) {
      const linked = allActivities.find(a => a.id === activityId);
      if (linked) setLinkedActivities(prev => [...prev, linked]);
    }
  };

  const handleUnlinkActivity = async (activityId: number) => {
    if (!provider || !currentOrg) return;
    const response = await serviceProviderApi.unlinkActivity(provider.id, activityId, currentOrg.id);
    if (response.success) {
      setLinkedActivities(prev => prev.filter(a => a.id !== activityId));
    }
  };

  const unlinkedActivities = allActivities.filter(
    a => !linkedActivities.some(l => l.id === a.id)
  );

  const requiresAvv =
    provider?.role === ServiceProviderRole.PROCESSOR ||
    provider?.role === ServiceProviderRole.JOINT_CONTROLLER;

  const activeAvv = avvContracts.find(a => a.status === AvvStatus.ACTIVE);
  const avvStatus = activeAvv
    ? 'active'
    : avvContracts.length > 0
    ? avvContracts[avvContracts.length - 1].status
    : null;

  if (loading || !provider) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  const renderOverviewTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Basic Information */}
      <Card header={<CardHeader titleText="Grundinformationen" avatar={<Icon name="business-card" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <InfoRow label="Name">
            <Text style={{ fontWeight: '500' }}>{provider.name}</Text>
          </InfoRow>
          <InfoRow label="Adresse">
            <Text>{provider.address || '-'}</Text>
          </InfoRow>
          <InfoRow label="Kontaktperson">
            <Text>{provider.contact_person || '-'}</Text>
          </InfoRow>
          <InfoRow label="E-Mail">
            {provider.contact_email ? (
              <a
                href={`mailto:${provider.contact_email}`}
                style={{ color: 'var(--sapLinkColor)', textDecoration: 'none', fontSize: '0.875rem' }}
              >
                {provider.contact_email}
              </a>
            ) : (
              <Text>-</Text>
            )}
          </InfoRow>
          <InfoRow label="Telefon">
            <Text>{provider.contact_phone || '-'}</Text>
          </InfoRow>
          <InfoRow label="Rolle">
            <ObjectStatus state={roleConfig[provider.role].state as any} showDefaultIcon>
              {roleConfig[provider.role].label}
            </ObjectStatus>
          </InfoRow>
          <InfoRow label="Hosting-Standort">
            <Text>{provider.hosting_location || '-'}</Text>
          </InfoRow>
        </div>
      </Card>

      {/* Service Description */}
      <Card header={<CardHeader titleText="Leistungsbeschreibung" avatar={<Icon name="shipping-status" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <InfoRow label="Leistung">
            <Text>{provider.service_description || '-'}</Text>
          </InfoRow>
          <InfoRow label="Notizen">
            <Text>{provider.notes || '-'}</Text>
          </InfoRow>
        </div>
      </Card>
    </div>
  );

  const renderAvvTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* AVV warning if required and missing */}
      {requiresAvv && avvContracts.length === 0 && (
        <MessageStrip design="Warning" hideCloseButton>
          Kein AVV vorhanden - gemäß Art. 28 DSGVO ist ein AVV erforderlich
        </MessageStrip>
      )}

      {/* New AVV button */}
      <FlexBox justifyContent="End">
        <Button
          icon="add"
          design={showNewAvvForm ? 'Default' : 'Emphasized'}
          onClick={() => {
            setShowNewAvvForm(!showNewAvvForm);
            if (showNewAvvForm) setNewAvvForm(defaultAvvForm);
          }}
        >
          {showNewAvvForm ? 'Abbrechen' : 'Neuer AVV'}
        </Button>
      </FlexBox>

      {/* Inline New AVV Form */}
      {showNewAvvForm && (
        <Card header={<CardHeader titleText="Neuen AVV anlegen" avatar={<Icon name="add-document" />} />}>
          <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                  Version *
                </Text>
                <Input
                  value={newAvvForm.version}
                  placeholder="z.B. 1.0"
                  style={{ width: '100%' }}
                  onInput={(e: any) => setNewAvvForm(f => ({ ...f, version: e.target.value }))}
                />
              </div>
              <div>
                <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                  Startdatum *
                </Text>
                <Input
                  value={newAvvForm.start_date}
                  placeholder="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  onInput={(e: any) => setNewAvvForm(f => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                  Enddatum (leer = unbefristet)
                </Text>
                <Input
                  value={newAvvForm.end_date}
                  placeholder="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  onInput={(e: any) => setNewAvvForm(f => ({ ...f, end_date: e.target.value }))}
                />
              </div>
              <div>
                <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                  Dokumentenreferenz
                </Text>
                <Input
                  value={newAvvForm.document_reference}
                  placeholder="z.B. AVV-2024-001"
                  style={{ width: '100%' }}
                  onInput={(e: any) => setNewAvvForm(f => ({ ...f, document_reference: e.target.value }))}
                />
              </div>
              <div>
                <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                  Lokaler Pfad
                </Text>
                <Input
                  value={newAvvForm.local_path}
                  placeholder="z.B. C:\Dokumente\AVV\avv-anbieter.pdf"
                  style={{ width: '100%' }}
                  onInput={(e: any) => setNewAvvForm(f => ({ ...f, local_path: e.target.value }))}
                />
              </div>
            </div>

            {/* PDF Upload hint for new AVV */}
            <MessageStrip design="Information" hideCloseButton>
              PDF-Dokumente können nach dem Erstellen des AVV im Vertragsdetail hochgeladen werden.
            </MessageStrip>

            {/* Subcontractors */}
            <div>
              <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                Subunternehmer
              </Text>
              <FlexBox alignItems="Center" style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Input
                  value={subcontractorInput}
                  placeholder="Subunternehmer hinzufügen"
                  style={{ flex: 1 }}
                  onInput={(e: any) => { const v = e.target.value ?? ''; setSubcontractorInput(v); subInputRef.current = v; }}
                  onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubcontractor(); } }}
                />
                <Button icon="add" onClick={handleAddSubcontractor}>Hinzufügen</Button>
              </FlexBox>
              {newAvvForm.subcontractors.length > 0 && (
                <FlexBox wrap="Wrap" style={{ gap: '0.375rem' }}>
                  {newAvvForm.subcontractors.map(sub => (
                    <FlexBox
                      key={sub}
                      alignItems="Center"
                      style={{
                        gap: '0.25rem',
                        background: 'var(--sapNeutralBackground)',
                        borderRadius: '0.25rem',
                        padding: '0.1875rem 0.5rem',
                        border: '1px solid var(--sapGroup_ContentBorderColor)'
                      }}
                    >
                      <Text style={{ fontSize: '0.8125rem' }}>{sub}</Text>
                      <Button
                        icon="decline"
                        design="Transparent"
                        style={{ minWidth: 0, padding: '0 0.25rem', height: '1.25rem' }}
                        onClick={() => handleRemoveSubcontractor(sub)}
                      />
                    </FlexBox>
                  ))}
                </FlexBox>
              )}
            </div>

            {/* TOM Assessment */}
            <div>
              <Text style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>
                TOM-Bewertung
              </Text>
              <TextArea
                value={newAvvForm.tom_assessment}
                placeholder="Beschreibung der technischen und organisatorischen Maßnahmen..."
                rows={3}
                style={{ width: '100%' }}
                onInput={(e: any) => setNewAvvForm(f => ({ ...f, tom_assessment: e.target.value }))}
              />
            </div>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <CheckBox
                checked={newAvvForm.tom_adequate}
                onChange={(e: any) => setNewAvvForm(f => ({ ...f, tom_adequate: e.target.checked }))}
                text="TOM ausreichend"
              />
            </FlexBox>

            <FlexBox justifyContent="End" style={{ gap: '0.5rem', marginTop: '0.5rem' }}>
              <Button
                design="Transparent"
                onClick={() => { setShowNewAvvForm(false); setNewAvvForm(defaultAvvForm); }}
              >
                Abbrechen
              </Button>
              <Button
                design="Emphasized"
                icon="save"
                disabled={!newAvvForm.version || !newAvvForm.start_date || savingAvv}
                onClick={handleCreateAvv}
              >
                {savingAvv ? 'Speichern...' : 'AVV erstellen'}
              </Button>
            </FlexBox>
          </div>
        </Card>
      )}

      {/* AVV Contract List */}
      {avvContracts.length === 0 && !showNewAvvForm && (
        <Card>
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
            <Icon name="document" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
            <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
              Noch keine AVV-Verträge vorhanden
            </Text>
          </div>
        </Card>
      )}

      {avvContracts.map(avv => (
        <Card
          key={avv.id}
          header={
            <CardHeader
              titleText={`AVV ${editingAvvId === avv.id ? editAvvForm.version : avv.version}`}
              subtitleText={`Erstellt: ${new Date(avv.created_at).toLocaleDateString('de-DE')}`}
              action={
                <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
                  {editingAvvId !== avv.id && canEdit && (
                    <Button design="Transparent" icon="edit" onClick={() => startEditAvv(avv)}>Bearbeiten</Button>
                  )}
                  <ObjectStatus state={avvStatusConfig[avv.status].state as any} showDefaultIcon>
                    {avvStatusConfig[avv.status].label}
                  </ObjectStatus>
                </FlexBox>
              }
            />
          }
        >
          {editingAvvId === avv.id ? (
            /* Edit Mode */
            <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <FlexBox style={{ gap: '0.75rem' }} wrap="Wrap">
                <div style={{ flex: '1 1 200px' }}>
                  <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Version</Label>
                  <Input value={editAvvForm.version} onInput={(e: any) => setEditAvvForm(f => ({ ...f, version: e.target.value }))} style={{ width: '100%' }} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Startdatum</Label>
                  <Input value={editAvvForm.start_date} onInput={(e: any) => setEditAvvForm(f => ({ ...f, start_date: e.target.value }))} placeholder="YYYY-MM-DD" style={{ width: '100%' }} />
                </div>
              </FlexBox>
              <FlexBox style={{ gap: '0.75rem' }} wrap="Wrap">
                <div style={{ flex: '1 1 200px' }}>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Enddatum</Label>
                  <Input value={editAvvForm.end_date} onInput={(e: any) => setEditAvvForm(f => ({ ...f, end_date: e.target.value }))} placeholder="leer = unbefristet" style={{ width: '100%' }} />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Dokumentenreferenz</Label>
                  <Input value={editAvvForm.document_reference} onInput={(e: any) => setEditAvvForm(f => ({ ...f, document_reference: e.target.value }))} style={{ width: '100%' }} />
                </div>
              </FlexBox>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Lokaler Pfad</Label>
                <Input value={editAvvForm.local_path} onInput={(e: any) => setEditAvvForm(f => ({ ...f, local_path: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Subunternehmer</Label>
                <FlexBox style={{ gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Input value={editSubInput} onInput={(e: any) => { const v = e.target.value ?? ''; setEditSubInput(v); editSubRef.current = v; }} placeholder="Subunternehmer hinzufügen" style={{ flex: 1 }} onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEditSub(); } }} />
                  <Button icon="add" onClick={handleAddEditSub}>Hinzufügen</Button>
                </FlexBox>
                {editAvvForm.subcontractors.length > 0 && (
                  <FlexBox wrap="Wrap" style={{ gap: '0.375rem' }}>
                    {editAvvForm.subcontractors.map(sub => (
                      <FlexBox key={sub} alignItems="Center" style={{ gap: '0.25rem', background: 'var(--sapNeutralBackground)', borderRadius: '0.25rem', padding: '0.1875rem 0.5rem', border: '1px solid var(--sapGroup_ContentBorderColor)' }}>
                        <Text style={{ fontSize: '0.8125rem' }}>{sub}</Text>
                        <Button icon="decline" design="Transparent" style={{ minWidth: 0 }} onClick={() => setEditAvvForm(f => ({ ...f, subcontractors: f.subcontractors.filter(s => s !== sub) }))} />
                      </FlexBox>
                    ))}
                  </FlexBox>
                )}
              </div>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.25rem' }}>TOM-Bewertung</Label>
                <TextArea value={editAvvForm.tom_assessment} onInput={(e: any) => setEditAvvForm(f => ({ ...f, tom_assessment: e.target.value }))} rows={3} style={{ width: '100%' }} />
              </div>
              <CheckBox checked={editAvvForm.tom_adequate} onChange={(e: any) => setEditAvvForm(f => ({ ...f, tom_adequate: e.target.checked }))} text="TOM ausreichend" />
              <FlexBox justifyContent="End" style={{ gap: '0.5rem', marginTop: '0.5rem' }}>
                <Button design="Transparent" onClick={() => setEditingAvvId(null)}>Abbrechen</Button>
                <Button design="Emphasized" icon="save" disabled={savingAvv} onClick={handleSaveAvvEdit}>
                  {savingAvv ? 'Speichern...' : 'Speichern'}
                </Button>
              </FlexBox>
            </div>
          ) : (
          /* View Mode */
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <InfoRow label="Vertragsbeginn">
              <Text>{new Date(avv.start_date).toLocaleDateString('de-DE')}</Text>
            </InfoRow>
            <InfoRow label="Vertragsende">
              {avv.end_date ? (
                <Text>{new Date(avv.end_date).toLocaleDateString('de-DE')}</Text>
              ) : (
                <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>Unbefristet</Text>
              )}
            </InfoRow>
            <InfoRow label="Status">
              <ObjectStatus state={avvStatusConfig[avv.status].state as any} showDefaultIcon>
                {avvStatusConfig[avv.status].label}
              </ObjectStatus>
            </InfoRow>
            <InfoRow label="Subunternehmer">
              {avv.subcontractors && avv.subcontractors.length > 0 ? (
                <Text>{avv.subcontractors.join(', ')}</Text>
              ) : (
                <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>Keine</Text>
              )}
            </InfoRow>
            <InfoRow label="TOM-Bewertung">
              <Text>{avv.tom_assessment || '-'}</Text>
            </InfoRow>
            <InfoRow label="TOM ausreichend">
              <ObjectStatus state={avv.tom_adequate ? 'Success' : 'Error'} showDefaultIcon>
                {avv.tom_adequate ? 'Ja' : 'Nein'}
              </ObjectStatus>
            </InfoRow>
            <InfoRow label="Dokumentenreferenz">
              <Text>{avv.document_reference || '-'}</Text>
            </InfoRow>
            <InfoRow label="Lokaler Pfad">
              <Text style={{ wordBreak: 'break-all' }}>{avv.local_path || '-'}</Text>
            </InfoRow>

            {/* PDF Documents */}
            <InfoRow label="Dokumente">
              <div>
                {avv.documents && avv.documents.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
                    {avv.documents.map(doc => (
                      <FlexBox
                        key={doc.path}
                        alignItems="Center"
                        justifyContent="SpaceBetween"
                        style={{
                          padding: '0.375rem 0.625rem',
                          borderRadius: '0.25rem',
                          border: '1px solid var(--sapGroup_ContentBorderColor)',
                          background: 'var(--sapNeutralBackground)'
                        }}
                      >
                        <FlexBox alignItems="Center" style={{ gap: '0.5rem', flex: 1, overflow: 'hidden' }}>
                          <Icon name="pdf-attachment" style={{ color: 'var(--sapNegativeColor)', flexShrink: 0 }} />
                          <a
                            href={currentOrg ? avvContractApi.getDownloadUrl(provider!.id, avv.id, currentOrg.id, doc.path) : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: 'var(--sapLinkColor)',
                              textDecoration: 'none',
                              fontSize: '0.8125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            onClick={(e) => {
                              // Attach token via header is not possible with plain anchor.
                              // Use fetch-based download for authenticated requests.
                              e.preventDefault();
                              if (!currentOrg) return;
                              const url = avvContractApi.getDownloadUrl(provider!.id, avv.id, currentOrg.id, doc.path);
                              const token = avvContractApi.getToken();
                              fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                                .then(r => r.blob())
                                .then(blob => {
                                  const a = document.createElement('a');
                                  a.href = URL.createObjectURL(blob);
                                  a.download = doc.name;
                                  a.click();
                                  URL.revokeObjectURL(a.href);
                                });
                            }}
                          >
                            {doc.name}
                          </a>
                        </FlexBox>
                        <Button
                          icon="delete"
                          design="Transparent"
                          tooltip="Dokument löschen"
                          style={{ color: 'var(--sapNegativeColor)', flexShrink: 0 }}
                          onClick={() => handleDeleteDocument(avv.id, doc.path)}
                        />
                      </FlexBox>
                    ))}
                  </div>
                ) : (
                  <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic', fontSize: '0.8125rem' }}>
                    Keine Dokumente hochgeladen
                  </Text>
                )}

                {/* File upload area */}
                <div style={{ marginTop: '0.5rem' }}>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    style={{ display: 'none' }}
                    id={`avv-upload-${avv.id}`}
                    onChange={(e) => handleUploadDocuments(avv.id, e.target.files)}
                  />
                  <label htmlFor={`avv-upload-${avv.id}`}>
                    <Button
                      icon="upload"
                      design="Default"
                      disabled={uploadingAvvId === avv.id}
                      onClick={() => document.getElementById(`avv-upload-${avv.id}`)?.click()}
                    >
                      {uploadingAvvId === avv.id ? 'Hochladen...' : 'PDF hochladen'}
                    </Button>
                  </label>
                </div>
              </div>
            </InfoRow>

            {/* Status Actions */}
            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
              <FlexBox alignItems="Center" justifyContent="SpaceBetween" wrap="Wrap" style={{ gap: '0.5rem' }}>
                <FlexBox style={{ gap: '0.5rem' }} wrap="Wrap">
                  {avv.status === AvvStatus.DRAFT && canEdit && (
                    <Button
                      design="Emphasized"
                      icon="accept"
                      onClick={() => handleAvvStatusChange(avv.id, AvvStatus.ACTIVE)}
                    >
                      Aktivieren
                    </Button>
                  )}
                  {avv.status === AvvStatus.ACTIVE && canEdit && (
                    <Button
                      design="Attention"
                      icon="cancel"
                      onClick={() => handleAvvStatusChange(avv.id, AvvStatus.TERMINATED)}
                    >
                      Beenden
                    </Button>
                  )}
                  {(avv.status === AvvStatus.EXPIRED || avv.status === AvvStatus.TERMINATED) && canEdit && (
                    <Button
                      icon="undo"
                      onClick={() => handleAvvStatusChange(avv.id, AvvStatus.DRAFT)}
                    >
                      Als Entwurf reaktivieren
                    </Button>
                  )}
                </FlexBox>
                {canDelete && (
                  <Button
                    design="Negative"
                    icon="delete"
                    onClick={() => setAvvToDelete(avv.id)}
                  >
                    Löschen
                  </Button>
                )}
              </FlexBox>
            </div>
          </div>
          )}
        </Card>
      ))}
    </div>
  );

  const renderProcessingTab = () => (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <FlexBox justifyContent="End">
        <Button
          icon="chain-link"
          onClick={() => setShowLinkDialog(true)}
          disabled={unlinkedActivities.length === 0}
        >
          Verknüpfung hinzufügen
        </Button>
      </FlexBox>

      {linkedActivities.length === 0 ? (
        <Card>
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center' }}>
            <Icon name="documents" style={{ fontSize: '2rem', color: 'var(--sapNeutralTextColor)', display: 'block', margin: '0 auto 0.75rem' }} />
            <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
              Keine verknüpften Verarbeitungstätigkeiten
            </Text>
          </div>
        </Card>
      ) : (
        <Card>
          <Table
            columns={
              <>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Name</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Abteilung</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Status</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Risiko</Text></TableColumn>
                <TableColumn><Text style={{ fontWeight: '600', fontSize: '0.8125rem' }}>Aktionen</Text></TableColumn>
              </>
            }
          >
            {linkedActivities.map(activity => (
              <TableRow
                key={activity.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/processing/${activity.id}`)}
              >
                <TableCell>
                  <Text style={{ fontWeight: '500', color: 'var(--sapLinkColor)' }}>{activity.name}</Text>
                </TableCell>
                <TableCell>
                  <Text>{activity.department}</Text>
                </TableCell>
                <TableCell>
                  <ObjectStatus state={processingStatusConfig[activity.status].state as any} showDefaultIcon>
                    {processingStatusConfig[activity.status].label}
                  </ObjectStatus>
                </TableCell>
                <TableCell>
                  {activity.risk_level ? (
                    <ObjectStatus state={riskConfig[activity.risk_level].state as any} showDefaultIcon>
                      {riskConfig[activity.risk_level].label}
                    </ObjectStatus>
                  ) : (
                    <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic', fontSize: '0.8125rem' }}>-</Text>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    design="Transparent"
                    icon="chain-link"
                    style={{ color: 'var(--sapNegativeColor)' }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleUnlinkActivity(activity.id);
                    }}
                  >
                    Entfernen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </Card>
      )}

      {/* Link Dialog - Inline selection */}
      {showLinkDialog && (
        <Card header={<CardHeader titleText="Verarbeitungstätigkeit verknüpfen" avatar={<Icon name="chain-link" />} />}>
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            {unlinkedActivities.length === 0 ? (
              <Text style={{ color: 'var(--sapNeutralTextColor)', fontStyle: 'italic' }}>
                Alle Verarbeitungstätigkeiten sind bereits verknüpft.
              </Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {unlinkedActivities.map(activity => (
                  <FlexBox
                    key={activity.id}
                    alignItems="Center"
                    justifyContent="SpaceBetween"
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                      background: 'var(--sapBaseColor)'
                    }}
                  >
                    <div>
                      <Text style={{ fontWeight: '500', display: 'block' }}>{activity.name}</Text>
                      <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                        {activity.department} · {processingStatusConfig[activity.status].label}
                      </Text>
                    </div>
                    <Button
                      icon="add"
                      design="Emphasized"
                      onClick={() => {
                        handleLinkActivity(activity.id);
                      }}
                    >
                      Verknüpfen
                    </Button>
                  </FlexBox>
                ))}
              </div>
            )}
            <FlexBox justifyContent="End" style={{ marginTop: '1rem' }}>
              <Button design="Transparent" onClick={() => setShowLinkDialog(false)}>Schließen</Button>
            </FlexBox>
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <div className="page-layout--detail">
      {/* Back link */}
      <Button
        icon="nav-back"
        design="Transparent"
        onClick={() => navigate('/service-providers')}
        style={{ marginBottom: '0.75rem' }}
      >
        Zurück zur Übersicht
      </Button>

      {/* Object Header Card */}
      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <FlexBox justifyContent="SpaceBetween" alignItems="Start" wrap="Wrap" style={{ gap: '1rem' }}>
            <div style={{ flex: '1 1 auto' }}>
              <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.5rem' }}>
                <Title level="H3" style={{ margin: 0 }}>{provider.name}</Title>
                <ObjectStatus state={roleConfig[provider.role].state as any} showDefaultIcon>
                  {roleConfig[provider.role].label}
                </ObjectStatus>
              </FlexBox>

              {/* Key attributes row */}
              <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginTop: '0.75rem' }}>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="map" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    {provider.hosting_location || 'Standort: -'}
                  </Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="employee" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    {roleConfig[provider.role].label}
                  </Text>
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="official-document" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  {avvStatus ? (
                    <ObjectStatus state={avvStatusConfig[avvStatus as AvvStatus].state as any} showDefaultIcon>
                      AVV: {avvStatusConfig[avvStatus as AvvStatus].label}
                    </ObjectStatus>
                  ) : (
                    <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>AVV: -</Text>
                  )}
                </FlexBox>
                <FlexBox alignItems="Center" style={{ gap: '0.375rem' }}>
                  <Icon name="calendar" style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem' }} />
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                    {new Date(provider.updated_at).toLocaleDateString('de-DE')}
                  </Text>
                </FlexBox>
              </FlexBox>
            </div>

            {/* Actions */}
            <FlexBox style={{ gap: '0.5rem' }} wrap="Wrap">
              <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('service-providers/' + id, currentOrg.id)}>PDF</Button>
              {canEdit && <Button icon="edit" onClick={handleEdit}>Bearbeiten</Button>}
              {canDelete && <Button icon="delete" design="Negative" onClick={() => setShowDeleteDialog(true)}>Löschen</Button>}
            </FlexBox>
          </FlexBox>
        </div>
      </Card>

      {/* Tabs */}
      <TabContainer>
        <Tab text="Überblick" icon="hint" selected>
          {renderOverviewTab()}
        </Tab>
        <Tab text="AVV-Verträge" icon="official-document">
          {renderAvvTab()}
        </Tab>
        <Tab text="Verarbeitungen" icon="documents">
          {renderProcessingTab()}
        </Tab>
      </TabContainer>

      {/* Delete Provider Dialog */}
      {showDeleteDialog && (
        <MessageBox
          open={showDeleteDialog}
          onClose={(e: any) => {
            setShowDeleteDialog(false);
            if (e.detail.action === 'OK') handleDelete();
          }}
          type="Confirm"
          titleText="Dienstleister löschen?"
        >
          Möchten Sie diesen Dienstleister wirklich löschen? Alle zugehörigen AVV-Verträge werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
        </MessageBox>
      )}

      {/* Delete AVV Dialog */}
      {avvToDelete !== null && (
        <MessageBox
          open={avvToDelete !== null}
          onClose={(e: any) => {
            const action = e.detail.action;
            setAvvToDelete(null);
            if (action === 'OK') handleAvvDelete();
          }}
          type="Confirm"
          titleText="AVV löschen?"
        >
          Möchten Sie diesen AVV-Vertrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </MessageBox>
      )}
    </div>
  );
}
